import { createStore, applyMiddleware, compose } from 'redux';
import ActionCableConnector from '../helpers/ActionCableConnector/ActionCableConnector';
import globalService from '../services/globalService.js';
import attachmentsService from '../services/attachmentsService';
import SessionManager from '../helpers/session/SessionManager';
import thunk from 'redux-thunk';
import reducers from './';
import DirectoriesDAO from '../dao/DirectoriesDAO';
import ProjectsDAO from '../dao/ProjectsDAO';
import ProjectTypesDAO from '../dao/ProjectTypesDAO';
import ProjectTemplatesDAO from '../dao/ProjectTemplatesDAO';
import ProjectChartSummaryDAO from '../dao/ProjectChartSummaryDAO';
import MarketsDAO from '../dao/MarketsDAO';
import UsersDAO from '../dao/UsersDAO';
import TeamsDAO from '../dao/TeamsDAO';
import AnnouncementsDAO from '../dao/AnnouncementsDAO';
import LocationsDAO from '../dao/LocationsDAO';
import TasksDAO from '../dao/TasksDAO';
import ReferenceDocumentsDAO from '../dao/ReferenceDocumentsDAO';
import UniqueDocumentsDAO from '../dao/UniqueDocumentsDAO';
import CompletionCriteriaDAO from '../dao/CompletionCriteriaDAO';
import CollectionsDAO from '../dao/CollectionsDAO';
import AttachmentsDAO from '../dao/AttachmentsDAO';
import InventoryItemsDAO from '../dao/InventoryItemsDAO';
import InventoryDAO from '../dao/InventoryDAO';
import EventHistoriesDAO from '../dao/EventHistoriesDAO';
import DelaysDAO from '../dao/DelaysDAO';
import CustomersDAO from '../dao/CustomersDAO';
import CommentsDAO from '../dao/CommentsDAO';
import CompaniesDAO from '../dao/CompaniesDAO';
import { userHasInsightsOnlyRole, setInsightsOnlySessionFromUser, INSIGHTS_DEFAULT_ROUTE } from '../helpers/user/insightsRoleAccess';
import CustomProjectSettingsDAO from '../dao/CustomProjectSettingsDAO';
import CloseoutsDAO from '../dao/CloseoutsDAO';
import progressReportsDAO from '../dao/ProgressReportsDAO';
import ActivitiesDAO from '../dao/ActivitiesDAO';
import ActivitySubTypesDAO from '../dao/ActivitySubTypesDAO';
import AlertsDAO from '../dao/AlertsDAO';
import FormFieldsDAO from '../dao/FormFieldsDAO';
import SupportRequestsDAO from '../dao/SupportRequestsDAO';
import LocationUsersDAO from '../dao/LocationUsersDAO';
import ProjectUsersDAO from '../dao/ProjectUsersDAO';
import ProjectContactsDAO from '../dao/ProjectContactsDAO';
import RolesDAO from '../dao/RolesDAO';
import UserTaskAssignmentsDAO from '../dao/UserTaskAssignmentsDAO';
import UserBuildAssignmentsDAO from '../dao/UserBuildAssignmentsDAO';
import PageStateDAO from '../dao/PageStateDAO';
import StoreManager from '../dao/StoreManager';
import AssignedProjectsCollection from '../dao/collections/AssignedProjectsCollection';
import offlineQueue from '../dao/queue/OfflineQueue';
import NotificationsDataModel from './NotificationsDataModel';
import ReferenceDocumentsDataModel from './ReferenceDocumentsDataModel';
import ActivityLogsDataModel from './ActivityLogsDataModel';
import ProjectsDataModel from './ProjectsDataModel';
import Analytics from '../helpers/analytics/analytics';
import ActivityDetection from '../helpers/activityDetection';
import Metadata from '../helpers/metadata/Metadata';
import mobileBridgeInstance from '../helpers/MobileBridge';
import connectivityHeartbeat from '../helpers/ConnectivityHeartbeat';
import logErrorForCloudWatch from '../helpers/awsCloudwatchLogs';

import ModalBannerConstants from '../constants/ModalBannerConstants';
import { persistStore, persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import BuildsDAO from '../dao/BuildsDAO';
import CompanyVendorUsersDAO from '../dao/CompanyVendorUsersDAO';
import { RecordStates } from '../constants/ReduxConstants';
import axios from 'axios';
import axiosRetry from '../helpers/OurAxiosRetry';
import serviceUtil from '../services/serviceUtil';
import AnalyticsDAO from '../dao/AnalyticsDAO';

const persistConfig = {
  key: 'active_oversight',
  storage,
  stateReconciler: autoMergeLevel2,
  version: StoreManager.version,
  // CAUTION: most of these names are stale and match no reducer. The refactored store renamed
  // the slices to `new_*` (see models/index.js), so only 'main', 'referenceDocuments' and
  // 'activityLogs' actually exclude anything -- redux logs the rest as unexpected keys.
  //
  // Do NOT "fix" the names without dealing with fromStoreOnly first. Correcting them stops
  // new_projects/new_builds being persisted, and DataSource.get() with fromStoreOnly=true reads
  // the store and never falls back to a fetch (dao/DataSource/DataSource.js). ProjectHeader
  // constructs its data sources that way, so after a reload it finds nothing, state.project stays
  // null and the header silently renders null. The persisted store is load-bearing here, not a
  // cache. Fixing this properly means giving fromStoreOnly a fetch fallback.
  blacklist: [
    'main',
    'project_types',
    'project_templates',
    'locations',
    'projects',
    'tasks',
    'builds',
    'directories',
    'markets',
    'referenceDocuments',
    'activityLogs',
    'analysisProjects',
    'analysisCompletionCriteria',
  ],
};

let middleware = [thunk];

let _store = null;
// Only keys that exist in combineReducers (models/index.js) belong here. The refactored store
// renamed most slices to `new_*`, and the stale names left behind -- directories, project_templates,
// projects, companies, users, tasks, builds, locations, markets -- matched no reducer, so redux
// discarded them and logged "Unexpected keys ... will be ignored" on every boot. The `new_*`
// reducers supply their own initial state, so there is nothing to carry over.
let initialState = {
  main: { globalModal: {}, globalBanner: {}, online: true },
  collections: {},
  notifications: { notifications: null },
  activityLogs: { activityLogs: null },
  activities: { activities: null },
  userState: {
    usersList: [],
    isFetching: false,
    searchUsersList: [],
    totalUsers: null,
    totalSearchUsers: null,
    pageNumber: 0,
  },
};

/*********************************************************
 *
 *  The data model
 *
 **********************************************************/

class DataModel {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor() {
    this.resetStore = this.resetStore.bind(this);
    this.handleExpiration = this.handleExpiration.bind(this);
    this.displayInfoErrorMessage = this.displayInfoErrorMessage.bind(this);
    this.displayHTTPError = this.displayHTTPError.bind(this);
    this.openActionCable = this.openActionCable.bind(this);
    this.closeActionCable = this.closeActionCable.bind(this);
    this.resetActionCable = this.resetActionCable.bind(this);
    this.onlineStatus = this.onlineStatus.bind(this);
    this.showGlobalBanner = this.showGlobalBanner.bind(this);
    this.hideGlobalBanner = this.hideGlobalBanner.bind(this);
    this.handleMobileAppResume = this.handleMobileAppResume.bind(this);
    this.initializeAlreadyLoggedIn = this.initializeAlreadyLoggedIn.bind(this);
    this.logoffStore = this.logoffStore.bind(this);
    this.resetStoreFromVersion = this.resetStoreFromVersion.bind(this);

    const spaVersion = process.env.VERSION;
    const storeSpaVersion = localStorage.spaVersion;
    let resetStore = false;
    if (spaVersion) {
      if (!storeSpaVersion) {
        resetStore = true;
      }
      else {
        const spaVersionParts = spaVersion.split('.');
        const storeSpaVersionParts = storeSpaVersion.split('.');
        resetStore = spaVersionParts[0] != storeSpaVersionParts[0] || spaVersionParts[1] != storeSpaVersionParts[1];
      }
    }
    localStorage.spaVersion = spaVersion;

    if (!this.store) {
      this.resetStore();
    }
    if (resetStore) {
      setTimeout(() => {
        this.resetStoreFromVersion();      
      }, 1);
    }
    
    if (!localStorage.token) {
      this.setNeedsLogin();
    }
    
    this.offlineQueue = offlineQueue;
    
    this.analytics = new Analytics();
    this.actionCableConnector = new ActionCableConnector();
    this.activityDetection = new ActivityDetection(this);

    connectivityHeartbeat.addObserver(this.onlineStatus);

    this.mobileBridge = mobileBridgeInstance;
    if (this.mobileBridge.isMobile && localStorage.token) {
      setTimeout(() => { this.handleMobileAppResume(true); }, 10);
    }
    else if (localStorage.token && localStorage.current_company_id) {
      setTimeout(() => { this.initializeAlreadyLoggedIn(); }, 10);
    }

    // Guard against another browser window/tab changing the shared session
    // (a different account logging in, or logging out) while this window still
    // shows the previous account. The storage event fires only in other
    // windows. See SessionManager.handleForeignSessionChange.
    // The handler is resolved lazily (inside the callback) so a circular
    // import between DataModel and SessionManager can't leave it undefined at
    // construction time.
    window.addEventListener('storage', (event) => {
      SessionManager.handleForeignSessionChange(event);
    });
    axiosRetry(axios, { retries: 5, retryDelay: axiosRetry.exponentialDelay, retryCondition: (error) => { 
      // route through our own checks (in front of axiosRetry) for any
      // special handling...
      return serviceUtil.isNetworkError(error) || serviceUtil.isIdempotentRequestError(error);
    } });
    // since some stuff is handled always on the offline queue we will
    // only show the sync banner when syncing after being offline
    this.showSyncBanner = false;
  }

  get store() {
    return _store;
  }

  set store(value) {
    _store = value;
  }

  resetStoreFromVersion() {
    this.storeManager.clearState({ newSpaVerion: true, clearAll: true });
  }

  async resetStore(reinitializing) {
    
    const persistedReducer = persistReducer(persistConfig, reducers);

    this.store = createStore(
      persistedReducer,
      initialState,
      window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__(),
      compose(
        applyMiddleware(...middleware),
      ));
    this.persistor = persistStore(this.store);

    this.store.stateTree = initialState;
    this.storeManager = new StoreManager(this.store);
    this.directoriesDAO = new DirectoriesDAO(this.store);
    this.projectsDAO = new ProjectsDAO(this.store);
    this.projectTypesDAO = new ProjectTypesDAO(this.store);
    this.projectTemplatesDAO = new ProjectTemplatesDAO(this.store);
    this.projectChartSummaryDAO = new ProjectChartSummaryDAO(this.store);
    this.buildsDAO = new BuildsDAO(this.store);
    this.marketsDAO = new MarketsDAO(this.store);
    this.usersDAO = new UsersDAO(this.store);
    this.teamsDAO = new TeamsDAO(this.store);
    this.announcementsDAO = new AnnouncementsDAO(this.store);
    this.locationsDAO = new LocationsDAO(this.store);
    this.tasksDAO = new TasksDAO(this.store);
    this.referenceDocumentsDAO = new ReferenceDocumentsDAO (this.store);
    this.uniqueDocumentsDAO = new UniqueDocumentsDAO (this.store);
    this.completionCriteriaDAO = new CompletionCriteriaDAO(this.store);
    this.commentsDAO = new CommentsDAO(this.store);
    this.delaysDAO = new DelaysDAO(this.store);
    this.customersDAO = new CustomersDAO(this.store);
    this.attachmentsDAO = new AttachmentsDAO(this.store);
    this.inventoryDAO = new InventoryDAO(this.store);
    this.inventoryItemsDAO = new InventoryItemsDAO(this.store);
    this.eventHistoriesDAO = new EventHistoriesDAO(this.store);
    this.companiesDAO = new CompaniesDAO(this.store);
    this.customProjectSettingsDAO = new CustomProjectSettingsDAO(this.store);
    this.collectionsDAO = new CollectionsDAO(this.store);
    this.closeoutsDAO = new CloseoutsDAO(this.store);
    this.progressReportsDAO = progressReportsDAO;
    this.notificationsDataModel = new NotificationsDataModel(this.store, this);
    this.referenceDocumentsDataModel = new ReferenceDocumentsDataModel(this.store, this);
    this.projectsDataModel = new ProjectsDataModel(this.store, this);
    this.activityLogsDataModel = new ActivityLogsDataModel(this.store, this);
    this.activitiesDAO = new ActivitiesDAO(this.store, this);
    this.activitySubTypesDAO = new ActivitySubTypesDAO(this.store, this);
    this.AlertsDAO = new AlertsDAO(this.store, this);
    this.formFieldsDAO = new FormFieldsDAO(this.store, this);
    this.supportRequestsDAO = new SupportRequestsDAO(this.store, this);
    this.locationUsersDAO = new LocationUsersDAO(this.store, this);
    this.companyVendorUsersDAO = new CompanyVendorUsersDAO(this.store, this);
    this.projectUsersDAO = new ProjectUsersDAO(this.store, this);
    this.projectContactsDAO = new ProjectContactsDAO(this.store, this);
    this.rolesDAO = new RolesDAO(this.store, this);
    this.userTaskAssignmentsDAO = new UserTaskAssignmentsDAO(this.store, this);
    this.userBuildAssignmentsDAO = new UserBuildAssignmentsDAO(this.store, this);
    this.AnalyticsDAO = AnalyticsDAO;
    this.PageStateDAO = PageStateDAO;
    this.mobileBridge = mobileBridgeInstance;
    this.semaphores = {}; //{fetcher: this.fetcher}

    this.usersDAO.quickAssignments = null;

    this.setSemaphore = this.setSemaphore.bind(this);
  }

  async logoffStore() {
    if (this.persistor) {
      this.storeManager.reset(null, false, { newUser: true, clearAll: true, clearDeprecated: true });
      await this.persistor.purge();
    }
  }

  resetSyncFlags(stateValues) {
    for (let key in stateValues) {
      if (key){
        if (key == 'collections' || key.indexOf('new_') == 0) {
          const object = stateValues[key];
          for (let subKey in object) {
            if (subKey && object[subKey]) {
              object[subKey]._recordState = RecordStates.NEEDS_SYNC;
            }
          }
        }
      }
    }
  }

  get stateTree() {
    if (this.store){
      return this.store.getState();
    }
    else{
      return null;
    }
  }

  setSemaphore(fetcher, addIn) {
    let name = fetcher.name;
    if (addIn) {
      name += addIn;
    }
    const matched = this.semaphores[name];
    if (matched) {
      // Expire it if necessary
      const now = new Date();
      if (Math.abs(now - matched.timeStamp) > 5000) {
        this.semaphores[name] = { fetcher: fetcher, timeStamp: new Date() };
        return name;
      }
      return null;
    }
    else {
      this.semaphores[name] = { fetcher: fetcher, timeStamp: new Date() };
      return name;
    }
  }

  clearSemaphore(semaphore) {
    this.semaphores[semaphore] = null;
  }

  handleExpiration() {
    this.activityDetection.deactivate();
    this.sessionTimeout = true;
  }

  openActionCable() {
    this.actionCableConnector.initialize();    
  }

  closeActionCable() {
    this.actionCableConnector.uninitialize();
  }

  resetActionCable() {
    this.actionCableConnector.uninitialize();
    this.actionCableConnector.initialize();
  }

  async handleMobileAppResume(checkExpiration, currentLocation) {
    // establish online/offline status
    connectivityHeartbeat.start(this);
    // check connectivity without invoking any observers please
    const isOnline = await connectivityHeartbeat.checkConnectivity(false);
    let token = localStorage.token;
    if (isOnline) {
    //connectivityHeartbeat.checkConnectivity().then((online) => {
      // TODO: see if we can split the two paths into separate functions
      if (checkExpiration) {
        // mobile app resume
        if (this.activityDetection.isSessionTokenExpired(localStorage.token)){
          token = await SessionManager.reissueToken();
        }
        this.activityDetection.activate(localStorage.token, this.handleExpiration, SessionManager.reissueToken);
        this.collectionsDAO.markCollectionNeedsSync();
        AnalyticsDAO.markTaskAnalyticsNeedsSync();
        if (currentLocation) {
          if (currentLocation.params) {
            let promises = [];
            for (let key in currentLocation.params) {
              if (key && typeof (currentLocation.params[key]) == 'number') {
                switch (key) {
                case 'project_id':
                  promises.push(this.projectsDAO.getProject(currentLocation.params[key], { force: true }));
                  break;
                case 'build_id':
                  promises.push(this.buildsDAO.getBuild(currentLocation.params[key], { force: true }));
                  break;
                case 'task_id':
                  promises.push(this.tasksDAO.getTask(currentLocation.params[key], { force: true }));
                  break;
                case 'completion_step_id':
                  promises.push(this.completionCriteriaDAO.getCompletionCriterion(currentLocation.params[key], { force: true }));
                  break;
                default:
                  break;
                }
              }
            }
            if (promises.length > 0) {
              Promise.all(promises).then(responses => {
                this.navigateUri = currentLocation.currentPath;
              });
            }
          }
        }
      }
      else {
        // mobile refresh with a pre-existing token
        this.setLoginSucceeded();
        if (this.activityDetection) {
        }
        Metadata.start();
        this.activityDetection.activate(localStorage.token, this.handleExpiration, SessionManager.reissueToken);
        
        this.collectionsDAO.markCollectionNeedsSync();
        AnalyticsDAO.markTaskAnalyticsNeedsSync();
      }
      if (this.mobileBridge.isMobile) {
        // ensure new token (if any) is communicated to the native app 
        // as javascript events may not be processed while app is paused
        // (hence the force = true flag)
        this.mobileBridge.setToken(token, true);
      }
//    })
    }
  }

  gotoDefaultPlace(loggedInUser) {
    if (userHasInsightsOnlyRole(loggedInUser)) {
      this.navigateUri = INSIGHTS_DEFAULT_ROUTE;
    }
    else if (loggedInUser.role_name == 'editor' || loggedInUser.role_name == 'admin') {
      this.navigateUri = '/manager-dashboard';
    }
    else if (!loggedInUser.role_name || loggedInUser.role_name == 'standard_user') {
      this.navigateUri = '/tasks/project/all';
    }
    else {
      this.navigateUri = '/projects/summary';
    }
  }

  // TODO: move this to SessionManager and user DAOs/Promises instead of callbacks
  initializeAlreadyLoggedIn() {
    if (localStorage.token && localStorage.current_company_id && localStorage.loggedin_user_id) {
      // fetch the current user
      this.usersDAO.getUser(+localStorage.loggedin_user_id).then(user => {
        if (user.error) {
          const errorInfoObject = this.buildDataModelErrorObjectForCloudwatch(user.error + ' -MobileTokenInvalidation', 'INFO_ERROR');
          logErrorForCloudWatch(errorInfoObject);
          this.setNeedsLogin();
        }
        else {
          if (user.role){
            user.role_name = user.role.name;
          }
          // Reload path: no login response ran, so establish the insights-only flag here, before
          // anything downstream issues a request the API denies to that role.
          setInsightsOnlySessionFromUser(user);
          this.notificationsDataModel.fetchNotifications();
          SessionManager.initializeCompany(user, this.mobileBridge.isMobile).then(async(currentCompany) => {
            if (!currentCompany || currentCompany.error) {
              const errorInfoObject = this.buildDataModelErrorObjectForCloudwatch(currentCompany ? currentCompany.error : 'Company Loading -MobileTokenInvalidation', 'INFO_ERROR');
              logErrorForCloudWatch(errorInfoObject);
              this.setNeedsLogin();
              return null;
            }
            else {
              connectivityHeartbeat.start(this);
              Metadata.start();
              this.activityDetection.activate(localStorage.token, this.handleExpiration, SessionManager.reissueToken);
              user = await this.usersDAO.getUser(user.id);
              if (user.role) {
                user.role_name = user.role.name;
              }
              if (!user.super_admin && user.companies && user.companies.length && user.companies[0].id != currentCompany.id) {
                user.isVendorUser = true;
              }
              this.usersDAO.currentUser = user;
              this.notificationsDataModel.fetchNotifications();
              this.displayCompanyBanners();
              if (!window.location.pathname || window.location.pathname == '/') {
                this.gotoDefaultPlace(user);
              }
              return user;
            }
          });
        }
      });
    }
    else {
      this.setNeedsLogin();
    }
  }

  /*************************************************************
   *
   *  Misc
   *
   *************************************************************/

  /*************************************************************
   *
   *  Support
   *
   *************************************************************/

  /** REDUX SETTERS/GETTERS */
  
  set inProgress(value) {
    const self = this;
    setTimeout(() => {
      self.store.dispatch({
        type: 'SET_IN_PROGRESS',
        inProgress: value,
      });
      if (value > 0) {
        if (self.progressTimer) {
          clearTimeout(self.progressTimer);
        }
        self.progressTimer = setTimeout(() => {
          self.inProgress = 0;
        }, value);
      }
    }, 1);
  }

  get inProgress() {
    return this.stateTree.main.inProgress;
  }

  set dataChanged(value) {
    // change it short way first, so it is available immediately, then broadcast it.
    this.stateTree.main.dataChanged = value;
    this.store.dispatch({
      type: 'SET_DATA_CHANGED',
      dataChanged: value,
    });
  }

  get dataChanged() {
    return this.stateTree.main.dataChanged;
  }

  set attachmentUploading(value) {
    // change it short way first, so it is available immediately, then broadcast it.
    this.stateTree.main.attachmentUploading = value;
    this.store.dispatch({
      type: 'SET_ATTACHMENT_UPLOADING',
      attachmentUploading: value,
    });
  }

  get attachmentUploading() {
    return this.stateTree.main.attachmentUploading;
  }

  set requiredDataNotAvailable(value) {
    // change it short way first, so it is available immediately, then broadcast it.
    this.stateTree.main.requiredDataNotAvailable = value;
    this.store.dispatch({
      type: 'SET_REQUIRED_DATA_NOT_AVAILABLE',
      requiredDataNotAvailable: value,
    });
  }

  get requiredDataNotAvailable() {
    return this.stateTree.main.requiredDataNotAvailable;
  }

  set breadCrumbsPath(breadCrumbsPath) {
    this.store.dispatch({
      type: 'SET_BREADCRUMBS_PATH',
      breadCrumbsPath: breadCrumbsPath,
    });
  }

  get breadCrumbsPath() {
    if (this.stateTree && this.stateTree.main){
      return this.stateTree.main.breadCrumbsPath;
    }
    else{
      return null;
    }
  }

  set searchResults(results) {
    this.store.dispatch({
      type: 'SET_SEARCH_RESULTS',
      results: results,
    });
  }

  set navigateUri(uri) {
    this.store.dispatch({
      type: 'SET_NAVIGATE_TO',
      navigateUri: uri,
    });
  }

  set returnUri(returnUri) {
    this.store.dispatch({
      type: 'SET_RETURN_URI',
      returnUri,
    });
  }

  setPassthroughUri(passthroughUri) {
    this.store.dispatch({
      type: 'SET_PASSTHROUGH_URI',
      passthroughUri: passthroughUri,
    });
  }

  set sessionTimeout(value) {
    this.store.dispatch({
      type: 'SET_SESSION_TIMEOUT',
      sessionTimeout: value,
    });
  }

  set needsPartialLogin(value) {
    if (value) {
      delete localStorage.loggedin_user_id;
    }
    this.store.dispatch({
      type: 'SET_NEEDS_PARTIAL_LOGIN',
      needsPartialLogin: value,
    });
  }

  setDisableAppBackground(value) {
    this.store.dispatch({
      type: 'SET_DISABLE_APP_BACKGROUND',
      disableAppBackground: value,
    });
  }

  set mobileReceipt(value) {
    this.store.dispatch({
      type: 'SET_MOBILE_RECEIPT',
      mobileReceipt: value,
    });
  }

  setActionCableUpdate() {
    this.store.dispatch({
      type: 'ACTION_CABLE_UPDATE',
      actionCableUpdate: !this.stateTree.main.actionCableUpdate,
    });
  }

  set catastrophicError(error) {
    this.store.dispatch({
      type: 'SET_CATASTROPHIC_ERROR',
      catastrophicError: error,
    });
  }

  triggerInfoErrorMessage(message, duration, severity, onDismissAction) {
    this.store.dispatch({
      type: 'SET_INFO_ERROR_MESSAGE',
      infoErrorMessage: { message: message, duration: duration, severity: severity, onDismissAction },
    });
  }

  dismissInfoErrorMessage() {
    this.store.dispatch({
      type: 'SET_INFO_ERROR_MESSAGE',
      infoErrorMessage: 'none',
    });
  }

  setNeedsLogin(ignoreCurrentPage) {
    // save the current path for after login
    if (!ignoreCurrentPage && window.location.pathname.indexOf('login') < 0
        && window.location.pathname.indexOf('index.html') < 0 && window.location.pathname != '/') {
      this.setPassthroughUri(window.location.pathname + (window.location.search ? window.location.search : ''));
    }

    this.store.dispatch({
      type: 'SET_NEEDS_LOGIN',
    });
  }

  set systemPopup(systemPopup) {
    this.store.dispatch({
      type: systemPopup ? 'DISPLAY_SYSTEM_POPUP' : 'DISMISS_SYSTEM_POPUP',
      systemPopup: systemPopup,
    });
  }

  setLoginSucceeded(value) {
    this.store.dispatch({
      type: 'SET_LOGIN_SUCCEEDED',
      value,
    });
  }

  setHandsFreeFlag(value) {
    this.store.dispatch({
      type: 'SET_HANDS_FREE_MODE',
      handsFreeMode: value,
    });
  }

  getHandsFreeFlag() {
    return this.stateTree.main.handsFreeMode;
  }

  /**
   * Render a <GlobalModal> of specific subtype into the root component.
   * @param {string} modalType - Which modal component to display. Must match a property of MODAL_COMPONENTS in GlobalModal
   * @param {object} modalProps - The props to pass to the specific modal component.
   */
  showGlobalModal(modalType, modalProps) {
    this.store.dispatch({
      type: 'SHOW_MODAL',
      modalType,
      modalProps,
    });
  }

  hideGlobalModal(modalType) {
    this.store.dispatch({
      type: 'HIDE_MODAL',
      modalType,
    });
  }
  
  showCircleGlobalModal(modalType, modalProps) {
    this.store.dispatch({
      type: 'SHOW_CIRCLE_MODAL',
      modalType,
      modalProps,
    });
  }

  hideCircleGlobalModal() {
    this.store.dispatch({
      type: 'HIDE_CIRCLE_MODAL',
    });
  }
  
  /**
   * Render a <GlobalBanner> of specific subtype into the root component.
   * @param {string} bannerType - Which banner component to display. Must match a property of BANNER_COMPONENTS in GlobalBanner
   * @param {object} bannerProps - The props to pass to the specific banner component.
   */
  showGlobalBanner(bannerType, bannerProps) {
    const { CLOSEOUT_NOTIFICATION_BANNER, PROJECTS_MIGRATED_BANNER,
      ZIP_DOWNLOAD_NOTIFICATION_BANNER, KMZ_DOWNLOAD_NOTIFICATION_BANNER } = ModalBannerConstants;
    // Banners that are specific to a given company
    const companyLevelBanners = new Set([CLOSEOUT_NOTIFICATION_BANNER, PROJECTS_MIGRATED_BANNER,
      ZIP_DOWNLOAD_NOTIFICATION_BANNER, KMZ_DOWNLOAD_NOTIFICATION_BANNER]);
    const currentUserId = localStorage.loggedin_user_id;
    if (companyLevelBanners.has(bannerType)) {
      // Ensure company banner is saved to userStorage[userId][companyBanners] in local storage 
      if (!localStorage.userStorage) {
        localStorage.userStorage = JSON.stringify({});
      }
      let userStorageParsed = JSON.parse(localStorage.userStorage);
      if (!userStorageParsed[currentUserId] || !userStorageParsed[currentUserId].companyBanners) {
        userStorageParsed[currentUserId] = { companyBanners: {} };
        localStorage.userStorage = JSON.stringify(userStorageParsed);
      }
      // re-parse in case data was changed in the if block above
      userStorageParsed = JSON.parse(localStorage.userStorage);
      const companyBanners = userStorageParsed[currentUserId].companyBanners;
      const currentCompanyName = this.stateTree.new_companies.currentCompany.name;
      const companyBannerPersistentState = { bannerType, bannerProps };
      if (!companyBanners[currentCompanyName]) {
        companyBanners[currentCompanyName] = {};
      }
      companyBanners[currentCompanyName][bannerType] = companyBannerPersistentState;
      userStorageParsed[currentUserId].companyBanners = companyBanners;
      localStorage.userStorage = JSON.stringify(userStorageParsed);
    }
    // Dispatch redux action
    this.store.dispatch({
      type: 'SHOW_BANNER',
      bannerType,
      bannerProps,
    });
  }

  /**
   * @param {boolean} shouldClearLocalStorage - if true - local banner storage will be cleared for the user
   */
  hideGlobalBanner(bannerType, shouldClearLocalStorage) {
    if (shouldClearLocalStorage) {
      const { CLOSEOUT_NOTIFICATION_BANNER, PROJECTS_MIGRATED_BANNER,
        ZIP_DOWNLOAD_NOTIFICATION_BANNER, KMZ_DOWNLOAD_NOTIFICATION_BANNER } = ModalBannerConstants;
      // Banners that are specific to a given company
      const companyLevelBanners = new Set([CLOSEOUT_NOTIFICATION_BANNER, PROJECTS_MIGRATED_BANNER,
        ZIP_DOWNLOAD_NOTIFICATION_BANNER, KMZ_DOWNLOAD_NOTIFICATION_BANNER]);
      const currentUserId = localStorage.loggedin_user_id;
      if (companyLevelBanners.has(bannerType)) {
        // clear company-level banner from local storage
        const userStorage = JSON.parse(localStorage.userStorage);
        if (userStorage[currentUserId] && userStorage[currentUserId].companyBanners) {
          delete userStorage[currentUserId].companyBanners[this.stateTree.new_companies.currentCompany.name][bannerType];
          localStorage.userStorage = JSON.stringify(userStorage);
        }
      }
    }

    // Dispatch redux action
    this.store.dispatch({
      type: 'HIDE_BANNER',
      bannerType,
    });
  }

  /** ERROR HANDLING */
  buildDataModelErrorObjectForCloudwatch(message, type, errorObj) {
    const appState = this.store.getState();
    const { currentUser } = appState.new_users;
    const { currentCompany } = appState.new_companies;

    const onMobile = this.mobileBridge.isMobile;

    let httpError = {};

    if(errorObj) {
      let request, response, source;
      if (errorObj.request) {
        request = JSON.parse(errorObj.request.response || errorObj.request);
        if (request.traces) {
          delete request['traces'];
        }
      }
      if (errorObj.response) {
        response = errorObj.response.data;
        if (response && response.traces) {
          delete response['traces'];
        }
      }
      if (errorObj.source) {
        source = errorObj.source;
      } else if (window.location.origin) {
        source = window.location.origin;
      }
      httpError = {
        url: errorObj.config ? errorObj.config.url : window.location.href,
        method: errorObj.config ? errorObj.config.method : '',
        message: errorObj.message || message,
        request: request || errorObj.stack,
        response: response || errorObj.stack,
        source: source || '',
      };
    }

    const errorInfoObject = {
      spaVersion: process.env.VERSION,
      type,
      message,
      currentUser,
      currentCompany,
      onMobile,
      httpError,
    };

    return errorInfoObject;
  }

  displayInfoErrorMessage(message, duration, severity, onDismissAction) {
    if (message == 'Network Error') {
      this.displayHTTPError('Unknown Status', 'Unexpected Network Error', true);
      return;
    }
    // Make sure the in-progress flag is reset on errors:

    if (severity > 1) {
      this.inProgress = 0;
    }
    if (severity == 2) {
      const errorInfoObject = this.buildDataModelErrorObjectForCloudwatch(message, 'INFO_ERROR');
      logErrorForCloudWatch(errorInfoObject);
    }
    // check for restriction on projects page when switching companies as super user
    if(message == 'Company restriction error' && window.location.href.includes('/companies')){
      // Other type of message if super user and is on companies page?
    }
    else{
      this.triggerInfoErrorMessage(message, duration, severity, onDismissAction);
    }
  }

  displayWarnErrorMessage(message, duration, severity, onDismissAction) {
    const errorInfoObject = this.buildDataModelErrorObjectForCloudwatch(message, 'WARN');
    logErrorForCloudWatch(errorInfoObject);
    this.triggerInfoErrorMessage(message, duration, severity, onDismissAction);
  }

  displayHTTPError(action, error, showStopper) {

    // if logged off, ignore any errors

    if (!localStorage.loggedin_user_id) {
      return;
    }
    let message = '';

    if (error.message == 'Network Error') {
      if (this.mobileBridge.isMobile) {
        return;
      }
      else {
        message= 'You are either currently offline or Active Oversight is not communicating.';
      }
    }
    if (error.code) {
      switch (error.code) {
      case 'ECONNABORTED':
        message = 'The request took too long to complete. Check your internet connection to ensure it is still active.';
        break;
      default:
      }
    }
    else if (error.response) {
      switch (error.response.status) {
      case 401:
        if (this.mobileBridge.isMobile) {
            // do not logoff if mobile...
        }
        else {
          SessionManager.logoff();
        }
        return;
      case 403:
        // The API member-scopes reads for standard users, so a 403 here means "not assigned to
        // this record" (e.g. a hand-typed URL for an unassigned project), not a broken request.
        message = 'You do not have access to this item, or this item does not exist';
        break;
      case 404:
        message = 'The requested resource was not found. If you continue to experience this error, please contact your company admin or customer support.';
        break;
      case 500:
        message = 'There was a failure on the web server. If you continue to experience this error, please contact your company admin or customer support.';
        break;
      default:
        message = error.message;
      }
    }
    else if (error.message && !message) {
      message = error.message;
    }
    else if (!message){
      message = error;
    }
    if (!showStopper) {
      this.displayInfoErrorMessage(action + ': ' + message, 4000, 2);
    }
    else {
      this.catastrophicError = { action: action, message: message };
      let errorObj;
      if (typeof(error) == 'string') {
        errorObj = new Error(`${message} Error occurred while performing action: ${action}`);
      } else {
        errorObj = error;
      }
      const errorInfoObject = this.buildDataModelErrorObjectForCloudwatch(message, 'CATASTROPHIC_ERROR', errorObj);
      logErrorForCloudWatch(errorInfoObject);
    }
  }

  /** MISC FUNCTIONS */
  syncData(){
    // this.analytics.event('Session Management', 'OfflineMode - Mobile', OfflineModeSyncConflict') 
    // ^ in future, when we have sync functionality, add this analytics logger to log sync conflict event 
    return new Promise((resolve) => {
      const assignedProjects = AssignedProjectsCollection.build();
      assignedProjects.fetchNext();
      AnalyticsDAO.markTaskAnalyticsNeedsSync();
      this.offlineQueue.processQueue().then((updatedData)=>{
        // wait to let other banner disappear
        setTimeout(()=> {
          resolve(updatedData);
        }, 1500);
      });
    });
  }

  notifyConnectionStatusChanged(online){
    const appState = this.store.getState();
    this.analytics.currentRole = appState.new_users.currentUser ? appState.new_users.currentUser.role : null;
    if (this.stateTree.main.online != online) {
      this.store.dispatch({
        type: 'CONNECTION_STATUS',
        online: online,
      });

      if (!online) {
        this.showGlobalBanner(ModalBannerConstants.MOBILE_OFFLINE_BANNER, { isMobile: true });
        this.analytics.event('Session Management', 'OfflineMode - Mobile', 'OfflineModeStart');
        this.showSyncBanner = true;
      }
      else {
        this.analytics.event('Session Management', 'OfflineMode - Mobile', 'OfflineModeEnd');
      
        // check for any offline actions were taken and then if there were sync that data
        if (this.offlineQueue.hasData()) {
          this.syncData().then((updatedData) => {
            const showSyncBanner = this.showSyncBanner;
            this.showSyncBanner = false;
            // NOTE: check online status again since we may be offline
            if (updatedData && showSyncBanner && this.stateTree.main.online) {
              this.showGlobalBanner(ModalBannerConstants.SYNC_SUCCESSFUL_BANNER, {
                isMobile: true,
              });
            }
          });
        }
      }
      return true;
    }
    else {
      if (online && this.offlineQueue.hasData()) {
        this.syncData().then((updatedData) => {
          const showSyncBanner = this.showSyncBanner;
          this.showSyncBanner = false;
          // NOTE: check online status again since we may be offline
          if (updatedData && showSyncBanner && this.stateTree.main.online) {
            this.showGlobalBanner(ModalBannerConstants.SYNC_SUCCESSFUL_BANNER, {
              isMobile: true,
            });
          }
        });
      }
      return false;
    }
  }

  displayCompanyBanners() {
    const currentUserId = localStorage.loggedin_user_id;
    const currentCompanyName = this.stateTree.new_companies.currentCompany ? this.stateTree.new_companies.currentCompany.name : '';
    // Retreive persistent company banners from local storage
    if (localStorage.userStorage) {
      const userStorage = JSON.parse(localStorage.userStorage);
      const currentUserStorage = userStorage[currentUserId];
      if (currentUserStorage && currentUserStorage.companyBanners && currentUserStorage.companyBanners[currentCompanyName]) {
        Object.entries(currentUserStorage.companyBanners[currentCompanyName]).forEach(banner => {
          this.showGlobalBanner(banner[1].bannerType, banner[1].bannerProps);
        });
      }
    }
  }

  set searchTerm(searchTerm) {
    globalService.getSearch(searchTerm, this.stateTree.new_companies.currentCompany.id).then(results => {
      this.searchResults = results;
    });
  }

  fetchAttachment(attachment_id, callBack) {
    attachmentsService.getAttachment(attachment_id).then(attachment => {
      if (attachment.error) {

      }
      else if (callBack) {
        callBack(attachment);
      }
    });
  }

  onlineStatus(online) {
    if (this.notifyConnectionStatusChanged(online)) {
      if (online) {
        console.log('retries = 5');
        axiosRetry(null, { retries: 5, retryDelay: axiosRetry.exponentialDelay, retryCondition: (error) => { 
          return serviceUtil.isNetworkError(error) || axiosRetry.isIdempotentRequestError(error);
        } });
      }
      else {
        // while we think we are offline perform no retries
        // lest we ruin the offline responsiveness
        console.log('retries = 0');
        axiosRetry(null, { retries: 0 });
      }
    }
  }

  get isOffline() {
    if (this.stateTree && this.stateTree.main){
      return this.stateTree.main.online == false;
    }
    else{
      return false;
    }
  }
}

export default new DataModel();



// WEBPACK FOOTER //
// ./src/models/DataModel.js